import { useState, useCallback } from 'react';
import type { JournalEntry, JournalEntryType, JournalVisibility, JournalData, JournalLink, JournalEntryOwner, ServerMessage } from '@roll1/shared';

type JournalChanges = Extract<ServerMessage, { type: 'journal:updated' }>['changes'];

export interface UseJournalReturn {
  entries: JournalEntry[];
  setEntries: React.Dispatch<React.SetStateAction<JournalEntry[]>>;
  // Server message handlers
  handleCreated: (entry: JournalEntry) => void;
  handleUpdated: (entryId: string, changes: JournalChanges) => void;
  handleDeleted: (entryId: string) => void;
  /** P03: whose the entry is (GM only echo). */
  handleOwnerChanged: (entryId: string, owner: JournalEntryOwner) => void;
  handleRevealed: (entry: JournalEntry) => void;
  handlePinned: (entryId: string, pinned: boolean) => void;
  handlePersonalCreated: (entry: JournalEntry) => void;
  handleLinked: (link: JournalLink) => void;
  handleUnlinked: (sourceId: string, targetId: string, relation: string) => void;
  // Action stubs (mp.send called from AppShell)
  createEntry: (entryType: JournalEntryType, name: string, tags?: string[], visibility?: JournalVisibility, data?: JournalData) => void;
  deleteEntry: (entryId: string) => void;
  revealEntry: (entryId: string) => void;
  pinEntry: (entryId: string, pinned: boolean) => void;
  createPersonalNote: (name: string, content: string) => void;
}

export function useJournal(): UseJournalReturn {
  const [entries, setEntries] = useState<JournalEntry[]>([]);

  const handleCreated = useCallback((entry: JournalEntry) => {
    setEntries(prev => {
      if (prev.some(e => e.id === entry.id)) return prev;
      return [...prev, entry];
    });
  }, []);

  const handleUpdated = useCallback((entryId: string, changes: JournalChanges) => {
    setEntries(prev => {
      const idx = prev.findIndex(e => e.id === entryId);
      if (idx === -1) return prev;
      return prev.map(e => {
        if (e.id !== entryId) return e;
        const updated: JournalEntry = { ...e, ...changes, pinId: changes.pinId === null ? undefined : changes.pinId ?? e.pinId, noteId: changes.noteId === null ? undefined : changes.noteId ?? e.noteId };
        // Deep-merge data so partial updates don't wipe existing fields
        if (changes.data) {
          updated.data = { ...e.data, ...changes.data } as JournalData;
        }
        return updated;
      });
    });
  }, []);

  const handleDeleted = useCallback((entryId: string) => {
    setEntries(prev => {
      if (!prev.some(e => e.id === entryId)) return prev;
      return prev.filter(e => e.id !== entryId).map(e => ({
        ...e,
        ...(e.linksFrom ? { linksFrom: e.linksFrom.filter(l => l.targetId !== entryId) } : {}),
        ...(e.linksTo ? { linksTo: e.linksTo.filter(l => l.sourceId !== entryId) } : {}),
      }));
    });
  }, []);

  const handleOwnerChanged = useCallback((entryId: string, owner: JournalEntryOwner) => {
    setEntries(prev => prev.map(e => (e.id === entryId ? { ...e, owner } : e)));
  }, []);

  const handleRevealed = useCallback((entry: JournalEntry) => {
    setEntries(prev => {
      const existing = prev.find(e => e.id === entry.id);
      if (existing) {
        return prev.map(e => e.id === entry.id ? { ...e, ...entry } : e);
      }
      return [...prev, entry];
    });
  }, []);

  const handlePinned = useCallback((entryId: string, pinned: boolean) => {
    setEntries(prev => prev.map(e => e.id === entryId ? { ...e, pinned } : e));
  }, []);

  const handlePersonalCreated = useCallback((entry: JournalEntry) => {
    setEntries(prev => {
      if (prev.some(e => e.id === entry.id)) return prev;
      return [...prev, entry];
    });
  }, []);

  const handleLinked = useCallback((link: JournalLink) => {
    setEntries(prev => prev.map(e => {
      if (e.id === link.sourceId) {
        const from = e.linksFrom ?? [];
        if (from.some(l => l.id === link.id)) return e;
        return { ...e, linksFrom: [...from, link] };
      }
      if (e.id === link.targetId) {
        const to = e.linksTo ?? [];
        if (to.some(l => l.id === link.id)) return e;
        return { ...e, linksTo: [...to, link] };
      }
      return e;
    }));
  }, []);

  const handleUnlinked = useCallback((sourceId: string, targetId: string, relation: string) => {
    setEntries(prev => prev.map(e => {
      if (e.id === sourceId && e.linksFrom) {
        const filtered = e.linksFrom.filter(l => !(l.targetId === targetId && l.relation === relation));
        if (filtered.length === e.linksFrom.length) return e;
        return { ...e, linksFrom: filtered };
      }
      if (e.id === targetId && e.linksTo) {
        const filtered = e.linksTo.filter(l => !(l.sourceId === sourceId && l.relation === relation));
        if (filtered.length === e.linksTo.length) return e;
        return { ...e, linksTo: filtered };
      }
      return e;
    }));
  }, []);

  // Action stubs — actual sending is done via mp.send in AppShell
  const createEntry = useCallback((_entryType: JournalEntryType, _name: string, _tags?: string[], _visibility?: JournalVisibility, _data?: JournalData) => {}, []);
  const deleteEntry = useCallback((_entryId: string) => {}, []);
  const revealEntry = useCallback((_entryId: string) => {}, []);
  const pinEntry = useCallback((_entryId: string, _pinned: boolean) => {}, []);
  const createPersonalNote = useCallback((_name: string, _content: string) => {}, []);

  return {
    entries,
    setEntries,
    handleCreated,
    handleUpdated,
    handleDeleted,
    handleOwnerChanged,
    handleRevealed,
    handlePinned,
    handlePersonalCreated,
    handleLinked,
    handleUnlinked,
    createEntry,
    deleteEntry,
    revealEntry,
    pinEntry,
    createPersonalNote,
  };
}
